import React from "react";
import CarteRepliable from "./CarteRepliable";
import {Button, Col, Container, FormGroup, Input, Label, Row} from "reactstrap";
import {inject, observer} from "mobx-react";
import StickMire, {zoneMorte as appliquerZoneMorte} from "../../../screen/StickMire";
import configuration from "../../../../configuration/configuration";
import ModalAkitatek from "../../../../components/Akitatek/Modal/Modal";
import WidgetSearch from "../../../../components/Akitatek/Form/Search/WidgetSearch";
import PickAsset from "../../Asset/PickAsset";
import {FONDS} from "../../../screen/fonds";
import {fondActuel, fondsEnRotation, secondesAvantChangement, secondesAvantRetour} from "../../../screen/fonds/choixFond";
import {horairesValides} from "../../../screen/fonds/ambiance";
import {aEnregistrer, resoudre as resoudreReglages, valeursParDefaut} from "../../../screen/fonds/reglages";
import ReglagesFond from "./ReglagesFond";
import ReglageSaisieClient from "./ReglageSaisieClient";
import EditeurPublicite from "./EditeurPublicite";

// Valeurs d'origine et limites (les mêmes que celles que vérifie le serveur : PUT /api/details)
const ZONE_MORTE = {defaut: 5, min: 0, max: 50};
const ZOOM = {defaut: 5, min: 2, max: 10};

const borne = (valeur, {min, max, defaut}) => {
    const n = Math.round(Number(valeur));
    return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : defaut;
};

/** Écart d'un stick « qui dérive », réglable pour montrer l'effet des réglages sans manette (aperçu seulement, non enregistré). */
const DERIVE = {defaut: 6, min: 0, max: 50};

// Rotation automatique des fonds du bandeau : durée d'affichage de chaque fond (mêmes limites que le serveur)
const ROTATION = {defaut: 10, min: 1, max: 120};

/** « 12 min 30 s », « 45 s » : durée lisible à partir d'un nombre de secondes. */
const duree = secondes => {
    const m = Math.floor(secondes / 60), s = secondes % 60;
    return m > 0 ? m + " min" + (s ? " " + s + " s" : "") : s + " s";
};

// Direction de la dérive d'exemple (vers le haut et la droite, comme un vrai stick qui tire d'un côté)
const DIRECTION = {x: 0.664, y: -0.747};

/**
 * Réglage de l'écran client « manette » : zone morte et zoom des mires des sticks.
 * Enregistré avec les paramètres de l'entreprise ; l'écran client en cours d'affichage se met à jour tout de suite.
 */
const EcranClient = (inject("rootStore")(observer(class EcranClient extends React.Component {

    get injected() {
        return this.props;
    }

    get auth() {
        return this.injected.rootStore.authStore;
    }

    constructor(props, context) {
        super(props, context);
        this.injected.rootStore.savedDatasStore.setHeader("Écran client", "Réglages de l'affichage des manettes", "default.jpg");

        this.state = {
            zoneMorte: borne(this.auth.stickZoneMorte, ZONE_MORTE),
            zoom: borne(this.auth.stickZoom, ZOOM),
            derive: DERIVE.defaut,
            enregistrement: false,
            rotation: borne(this.auth.fondRotationMinutes, ROTATION),
            horaires: {...this.auth.fondHoraires},   // heures de l'ambiance automatique en cours de modification
            maintenant: Date.now(),
            galerie: null,   // identifiant du fond pour lequel la galerie est ouverte
            brouillons: {},  // réglages en cours de modification, par fond (montrés dans l'aperçu, pas encore enregistrés)
        };
    }

    // Les durées affichées (retour à la rotation, prochain changement) se rafraîchissent d'elles-mêmes
    componentDidMount() {
        this.minuteur = window.setInterval(() => this.setState({maintenant: Date.now()}), 5000);
    }

    componentWillUnmount() {
        window.clearInterval(this.minuteur);
    }

    /** Choix manuel d'un fond (null : retour immédiat à la rotation) ; l'écran client change en direct. */
    async choisirFond(id) {
        try {
            await this.auth.choisirFond(id);
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Fond non modifié", "error");
        }
    }

    /** Active ou désactive la rotation automatique ; désactivée, l'écran garde le fond affiché. */
    async basculerRotationAuto(active) {
        try {
            await this.auth.updateDetails({fond_rotation_active: active});
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Rotation non modifiée", "error");
        }
    }

    /** Ajoute ou retire un fond de la rotation automatique (il reste choisissable à la main) ; l'écran client change en direct. */
    async basculerRotation(id, dedans) {
        const exclus = this.auth.fondExclus.filter(e => e !== id).concat(dedans ? [] : [id]);
        try {
            await this.auth.enregistrerFondsExclus(exclus);
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Rotation non modifiée", "error");
        }
    }

    /** Valeurs des réglages d'un fond : le brouillon s'il y en a un, sinon ce qui est enregistré. */
    reglagesDe(idFond) {
        return this.state.brouillons[idFond] || this.auth.fondReglages[idFond];
    }

    changerReglage(idFond, cle, valeur) {
        const base = resoudreReglages(FONDS[idFond].reglages, this.reglagesDe(idFond));
        this.setState({brouillons: {...this.state.brouillons, [idFond]: {...base, [cle]: valeur}}});
    }

    remettreOrigine(idFond) {
        this.setState({brouillons: {...this.state.brouillons, [idFond]: valeursParDefaut(FONDS[idFond].reglages)}});
    }

    async enregistrerReglages(idFond) {
        const schema = FONDS[idFond].reglages;
        try {
            await this.auth.enregistrerReglagesFond(idFond, aEnregistrer(schema, this.state.brouillons[idFond]));
            const {[idFond]: _, ...autres} = this.state.brouillons;
            this.setState({brouillons: autres});
            this.injected.rootStore.notificationStore.setNotification("Réglages du fond enregistrés", "success");
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification(typeof erreur === "string" ? erreur : "Réglages non enregistrés", "error");
        }
    }

    ouvrirGalerie(idFond) {
        this.injected.rootStore.imageStore.pickAsset = null;
        this.setState({galerie: idFond});
    }

    fermerGalerie = () => {
        this.setState({galerie: null});
    };

    /** Une image a été choisie (ou téléversée puis choisie) dans la galerie : on l'associe au fond. */
    imageChoisie = async () => {
        const imageStore = this.injected.rootStore.imageStore;
        const asset = imageStore.pickAsset;
        const fond = this.state.galerie;
        imageStore.pickAsset = null;
        this.setState({galerie: null});
        if (!asset || !asset.id || !fond) return;

        try {
            await this.auth.ajouterImageFond(fond, asset.id);
            this.injected.rootStore.notificationStore.setNotification("Image ajoutée au fond", "success");
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification(typeof erreur === "string" ? erreur : "Image non ajoutée", "error");
        }
    };

    async retirerImage(imageId) {
        try {
            await this.auth.retirerImageFond(imageId);
            this.injected.rootStore.notificationStore.setNotification("Image retirée du fond", "success");
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Image non retirée", "error");
        }
    }

    /** Images de la galerie associées à un fond : miniatures à retirer, et ajout depuis la galerie (téléversement compris). */
    imagesDuFond(idFond) {
        const liste = this.auth.fondImages[idFond] || [];
        return (
            <div className="mt-2">
                <small className="text-muted d-block mb-1">{FONDS[idFond].images} ({liste.length}) : elles défilent avec les manettes dessinées</small>
                <div className="d-flex flex-wrap align-items-center">
                    {liste.map(image => (
                        <div key={image.id} style={{position: "relative", margin: "0 8px 8px 0"}}>
                            <img src={configuration.API + image.url} alt={image.nom} title={image.nom} height={48}
                                 style={{background: "repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 12px 12px", borderRadius: 4, maxWidth: 96, objectFit: "contain"}}/>
                            <button type="button" className="btn btn-danger btn-sm p-0" title="Retirer cette image du fond"
                                    style={{position: "absolute", top: -6, right: -6, width: 20, height: 20, lineHeight: "18px", borderRadius: "50%"}}
                                    onClick={() => this.retirerImage(image.id)}>×</button>
                        </div>
                    ))}
                    <Button size="sm" color="secondary" className="mb-2" onClick={() => this.ouvrirGalerie(idFond)}>
                        Ajouter une image de la galerie
                    </Button>
                </div>
            </div>
        );
    }

    async enregistrerRotation(e) {
        e.preventDefault();
        try {
            await this.auth.updateDetails({fond_rotation_minutes: this.state.rotation});
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Rotation non enregistrée", "error");
        }
    }

    /** Enregistre les heures de l'ambiance automatique (jour, crépuscule, nuit) ; les fonds concernés changent en direct. */
    async enregistrerHoraires(e) {
        e.preventDefault();
        const {jour, crepuscule, nuit} = this.state.horaires;
        try {
            await this.auth.updateDetails({ambiance_jour: jour, ambiance_crepuscule: crepuscule, ambiance_nuit: nuit});
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Horaires non enregistrés", "error");
        }
    }

    changerHoraire(cle, valeur) {
        const n = Math.round(Number(valeur));
        this.setState({horaires: {...this.state.horaires, [cle]: Number.isFinite(n) ? Math.max(0, Math.min(23, n)) : 0}});
    }

    /** Carte « Fond du bandeau » : état actuel, choix manuel avec aperçu de chaque fond, réglage de la rotation. */
    carteFond() {
        const auth = this.auth;
        const ids = Object.keys(FONDS);
        const maintenant = this.state.maintenant;
        const reglage = {ids, rotationMinutes: auth.fondRotationMinutes, choisi: auth.fondChoisi, expireA: auth.fondExpireA, maintenant, exclus: auth.fondExclus, rotationActive: auth.fondRotationActive};
        const enRotation = fondsEnRotation(ids, auth.fondExclus);
        const affiche = fondActuel(reglage);
        const retour = secondesAvantRetour(reglage);
        const fixe = !auth.fondRotationActive;
        const manuel = !fixe && retour > 0 && affiche === auth.fondChoisi;
        const rotationEnregistree = borne(auth.fondRotationMinutes, ROTATION);

        return (
            <CarteRepliable id="fond-du-bandeau" titre="Fond du bandeau" className="mb-4">
                <>
                    <p>
                        {ids.length === 0 && "Aucun fond disponible."}
                        {ids.length > 0 && (fixe
                            ? <>Rotation automatique désactivée : <strong>{FONDS[affiche].nom}</strong> reste affiché, sans limite de durée. Choisissez un autre fond avec « Afficher ce fond ».</>
                            : manuel
                            ? <>Fond affiché : <strong>{FONDS[affiche].nom}</strong>, choisi à la main. Retour à la rotation automatique dans <strong>{duree(retour)}</strong>.</>
                            : enRotation.length > 1
                                ? <>Mode automatique : <strong>{FONDS[affiche].nom}</strong> est affiché, le fond change toutes les <strong>{rotationEnregistree} min</strong> entre <strong>{enRotation.length} fonds</strong> (prochain changement dans {duree(secondesAvantChangement({rotationMinutes: auth.fondRotationMinutes, maintenant}))}).</>
                                : <>Un seul fond est dans la rotation : <strong>{FONDS[affiche].nom}</strong>, il reste affiché. Cliquez sur « Hors rotation » d'autres fonds pour que l'écran change tout seul.</>)}
                    </p>

                    <Row>
                        {ids.map(id => (
                            <Col key={id} md="6" xl="4" className="mb-3">
                                <div style={{position: "relative", height: 112, overflow: "hidden", borderRadius: ".375rem", background: "#036"}}>
                                    {React.createElement(FONDS[id].composant, {images: (auth.fondImages[id] || []).map(i => configuration.API + i.url), reglages: this.reglagesDe(id), horaires: auth.fondHoraires})}
                                </div>
                                <div className="d-flex flex-wrap justify-content-between align-items-center mt-2">
                                    <strong>{FONDS[id].nom}{id === affiche && <span className="badge badge-success ml-2">affiché</span>}</strong>
                                    <div className="text-right">
                                        <Button size="sm" color={enRotation.includes(id) ? "success" : "secondary"} className="mr-1"
                                                disabled={enRotation.length === 1 && enRotation[0] === id}
                                                title={enRotation.length === 1 && enRotation[0] === id
                                                    ? "Il faut garder au moins un fond dans la rotation"
                                                    : enRotation.includes(id) ? "Cliquer pour retirer ce fond de la rotation automatique" : "Cliquer pour remettre ce fond dans la rotation automatique"}
                                                onClick={() => this.basculerRotation(id, !enRotation.includes(id))}>
                                            {enRotation.includes(id) ? "✔ Dans la rotation" : "✖ Hors rotation"}
                                        </Button>
                                        <Button size="sm" color="primary" disabled={(manuel || fixe) && id === affiche} onClick={() => this.choisirFond(id)}>
                                            Afficher ce fond
                                        </Button>
                                    </div>
                                </div>
                                {FONDS[id].images && this.imagesDuFond(id)}
                                {FONDS[id].reglages && (
                                    <ReglagesFond schema={FONDS[id].reglages} valeurs={this.reglagesDe(id)} enregistrees={auth.fondReglages[id]}
                                                  onChange={(cle, valeur) => this.changerReglage(id, cle, valeur)}
                                                  onEnregistrer={() => this.enregistrerReglages(id)}
                                                  onOrigine={() => this.remettreOrigine(id)}/>
                                )}
                            </Col>
                        ))}
                    </Row>
                    <small className="text-muted d-block mb-3">
                        « Afficher ce fond » change l'écran client tout de suite, en direct. {fixe ? "La rotation automatique est désactivée : le fond choisi reste." : "Au bout de 30 minutes, l'écran revient seul à la rotation automatique."}
                    </small>

                    {manuel && (
                        <Button color="secondary" className="mb-3" onClick={() => this.choisirFond(null)}>
                            Revenir à la rotation automatique maintenant
                        </Button>
                    )}

                    <ModalAkitatek show={!!this.state.galerie}
                                   title={<WidgetSearch icon={"fa fa-search bg-primary p-1 font-10xl mr-3 float-left"} col={"col-12"} category={"images"}/>}
                                   children={<PickAsset onCallback={this.imageChoisie}/>}
                                   onCallback={this.fermerGalerie}/>

                    <Button color={auth.fondRotationActive ? "success" : "secondary"} className="mb-3" onClick={() => this.basculerRotationAuto(!auth.fondRotationActive)}>
                        {auth.fondRotationActive ? "✔ Rotation automatique activée" : "✖ Rotation automatique désactivée"}
                    </Button>
                    <small className="text-muted d-block mb-3">
                        {auth.fondRotationActive
                            ? "Cliquez pour désactiver : l'écran garde alors le fond affiché, sans en changer."
                            : "Cliquez pour réactiver : les fonds cochés « Dans la rotation » défileront de nouveau."}
                    </small>

                    <form className="mb-4" onSubmit={e => this.enregistrerHoraires(e)}>
                        <Label><strong>Horaires de l'ambiance automatique</strong></Label>
                        <Row form>
                            {[["jour", "Jour dès"], ["crepuscule", "Crépuscule dès"], ["nuit", "Nuit dès"]].map(([cle, libelle]) => (
                                <Col key={cle} xs="4" md="3" lg="2">
                                    <Label for={"horaire-" + cle} className="small mb-0">{libelle}</Label>
                                    <Input type="number" bsSize="sm" id={"horaire-" + cle} min={0} max={23} step={1} value={this.state.horaires[cle]}
                                           onChange={e => this.changerHoraire(cle, e.target.value)}/>
                                </Col>
                            ))}
                        </Row>
                        <small className="text-muted d-block mb-2">
                            Heures pleines (0 à 23) à partir desquelles les fonds en ambiance « automatique » (Voyageur, Oiseau) passent en jour,
                            en crépuscule, puis en nuit jusqu'au jour suivant. Par défaut : jour dès 9 h, crépuscule dès 16 h, nuit dès 18 h
                            (le magasin ouvre de 9 h à 18 h).
                        </small>
                        {!horairesValides(this.state.horaires) && (
                            <small className="text-danger d-block mb-2">Les heures doivent se suivre : le jour, puis le crépuscule, puis la nuit.</small>
                        )}
                        <Button color="primary" type="submit" size="sm"
                                disabled={!horairesValides(this.state.horaires) || JSON.stringify(this.state.horaires) === JSON.stringify(auth.fondHoraires)}>
                            Enregistrer les horaires
                        </Button>
                    </form>

                    <form onSubmit={e => this.enregistrerRotation(e)}>
                        {this.champ("rotation", "Rotation automatique : un fond toutes les", ROTATION, "min",
                            "Durée d'affichage de chaque fond quand aucun n'est choisi à la main. Tous les écrans changent au même moment.")}
                        <Button color="primary" type="submit" disabled={this.state.rotation === rotationEnregistree}>
                            Enregistrer la rotation
                        </Button>
                    </form>
                </>
            </CarteRepliable>
        );
    }

    enregistre() {
        const {zoneMorte, zoom} = this.state;
        return zoneMorte === borne(this.auth.stickZoneMorte, ZONE_MORTE) && zoom === borne(this.auth.stickZoom, ZOOM);
    }

    async onSubmit(e) {
        e.preventDefault();
        this.setState({enregistrement: true});
        try {
            await this.auth.updateDetails({stick_zone_morte: this.state.zoneMorte, stick_zoom: this.state.zoom});
        } catch (erreur) {
            this.injected.rootStore.notificationStore.setNotification("Réglages non enregistrés", "error");
        }
        this.setState({enregistrement: false});
    }

    champ(cle, libelle, limites, unite, aide) {
        const valeur = this.state[cle];
        return (
            <FormGroup>
                <Label for={cle}><strong>{libelle}</strong> : {unite === "%" ? valeur + " %" : unite === "min" ? valeur + " min" : "×" + valeur}</Label>
                <Row form className="align-items-center">
                    <Col xs="9">
                        <Input type="range" id={cle} min={limites.min} max={limites.max} step={1} value={valeur}
                               onChange={e => this.setState({[cle]: borne(e.target.value, limites)})}/>
                    </Col>
                    <Col xs="3">
                        <Input type="number" min={limites.min} max={limites.max} step={1} value={valeur}
                               onChange={e => this.setState({[cle]: borne(e.target.value, limites)})}/>
                    </Col>
                </Row>
                <small className="text-muted">{aide}</small>
            </FormGroup>
        );
    }

    render() {
        const {zoneMorte, zoom, derive} = this.state;
        // même règle que sur l'écran client : au centre dans la tolérance, à sa vraie position au-delà
        const reelX = (derive / 100) * DIRECTION.x, reelY = (derive / 100) * DIRECTION.y;
        const [exempleX, exempleY] = appliquerZoneMorte(reelX, reelY, zoneMorte / 100);
        const exempleCentre = exempleX === 0 && exempleY === 0;

        return (
            <Container fluid>
                {this.carteFond()}
                <CarteRepliable id="mires-des-sticks" titre="Mires des sticks (écran client)">
                    <Row>
                        <Col lg="7">
                            <form onSubmit={e => this.onSubmit(e)}>
                                {this.champ("zoneMorte", "Tolérance de dérive (zone morte)", ZONE_MORTE, "%",
                                    "Écart au centre jugé normal : aucun joystick n'est parfait et la console ramène ce petit écart à 0 %. " +
                                    "Un stick relâché à moins de ce pourcentage est « Centré ✔ » et le point reste au centre ; au-delà, l'écran annonce la dérive. " +
                                    "C'est aussi la taille du cercle vert.")}
                                {this.champ("zoom", "Zoom au centre", ZOOM, "x",
                                    "Grossissement de la petite mire centrale : plus il est élevé, plus une dérive se voit.")}

                                <Button color="primary" type="submit" disabled={this.state.enregistrement || this.enregistre()}>
                                    Enregistrer
                                </Button>
                                <Button color="secondary" type="button" className="ml-2"
                                        onClick={() => this.setState({zoneMorte: ZONE_MORTE.defaut, zoom: ZOOM.defaut})}>
                                    Valeurs d'origine ({ZONE_MORTE.defaut} % et ×{ZOOM.defaut})
                                </Button>
                                <p className="text-muted mt-3 mb-0">
                                    Le réglage s'applique à l'écran client déjà ouvert, sans le recharger.
                                </p>
                            </form>
                        </Col>
                        <Col lg="5">
                            <h4 className="h5 mb-2">Aperçu</h4>
                            <div style={{background: "#0b1020", color: "#fff", borderRadius: ".375rem", padding: "1rem"}}>
                                <p className="small" style={{opacity: .8}}>
                                    Un stick relâché à {derive} % du centre : {exempleCentre ? "dans la tolérance, il est annoncé centré." : "au-dessus de la tolérance, la dérive est annoncée."}
                                    {" "}Le cercle vert est la tolérance (tracée ici seulement), et l'écart réel n'apparaît que dans cet aperçu, pas sur l'écran client.
                                </p>
                                <Label for="derive" className="small mb-0">Écart simulé : {derive} %</Label>
                                <Input type="range" id="derive" min={DERIVE.min} max={DERIVE.max} step={1} value={derive}
                                       className="mb-3"
                                       onChange={e => this.setState({derive: borne(e.target.value, DERIVE)})}/>
                                <StickMire titre="Exemple" zoneMorte={zoneMorte} zoom={zoom} apercu
                                           x={exempleX} y={exempleY} reel={[reelX, reelY]}/>
                            </div>
                        </Col>
                    </Row>
                </CarteRepliable>

                <EditeurPublicite/>

                <ReglageSaisieClient/>
            </Container>
        );
    }
})));

export default EcranClient;
